import { Stack, Text, Box } from '@mantine/core';
import { DndContext, closestCenter, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { PageElement } from '@holloway/dsl';
import DraggablePageElement from '../PageElements/DraggablePageElement';
import { renderElementContent } from '../PageElements/Renderers';

interface PageElementsCanvasProps {
  elements: PageElement[];
  /**
   * Current variables, so mentions in rich text render under their present
   * names. Without them the canvas falls back to the label stored inside the
   * content, which is whatever the variable was called when it was inserted.
   */
  variables?: { id: string; label: string }[];
  selectedElementId: string | null;
  sensors: any;
  onSelect: (id: string) => void;
  onRemove: (id: string) => void;
  onUpdate: (id: string, updates: Partial<PageElement>) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

export default function PageElementsCanvas({
  elements,
  variables = [],
  selectedElementId,
  sensors,
  onSelect,
  onRemove,
  onUpdate,
  onDragEnd,
}: PageElementsCanvasProps) {
  if (elements.length === 0) {
    return (
      <Box
        p="xl"
        style={{
          border: '2px dashed #D0D5DE',
          borderRadius: 12,
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
          minHeight: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text c="dimmed" size="sm">No components yet. Add from the library.</Text>
      </Box>
    );
  }

  return (
    <Box>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={elements.map((el) => el.id)} strategy={verticalListSortingStrategy}>
          <Stack gap="sm">
            {elements.map((element) => (
              <DraggablePageElement
                key={element.id}
                element={element}
                isSelected={selectedElementId === element.id}
                onSelect={() => onSelect(element.id)}
                onRemove={() => onRemove(element.id)}
              >
                {renderElementContent(element, (updates) => onUpdate(element.id, updates), variables)}
              </DraggablePageElement>
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Box>
  );
}
